import { useEffect, useRef, useState } from 'react';
import { soundsApi } from '@/lib/api/arrex/sounds';
import { settingsApi } from '@/lib/api/arrex/settings';
import { uploadImage } from '@/lib/upload';
import { resolveAssetUrl } from '@/lib/api/http';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ToggleField } from '@/components/ui/toggle-field';
import { Slider } from '@/components/ui/slider';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Collapse } from '@/components/ui/collapse';
import { Badge } from '@/components/ui/badge';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Play, Pause, Trash2, ArrowUp, ArrowDown, Upload, Music2, Wand2, Activity,
  Volume2, VolumeX, Loader2, Check,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { EVENT_KEYS, EventKey } from '@/lib/audio/engine';
import { useApp } from '@/contexts/AppContext';
import { cn } from '@/lib/utils';
import { readItems } from '@/lib/api/normalize';

import { FormSkeleton } from '@/components/ui/skeletons';
import { Swiper, SwiperSlide } from 'swiper/react';
import { FreeMode } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/free-mode';

type Track = { id: string; name: string; file_url: string; duration_seconds: number; sort_order: number; is_enabled: boolean };
type Sfx = { id: string; name: string; file_url: string; duration_seconds: number };
type EventMap = { event_key: string; sound_effect_id: string | null };

const EVENT_GROUPS: { key: string; events: EventKey[] }[] = [
  { key: 'Cart', events: ['add_to_cart', 'remove_from_cart', 'open_cart', 'checkout', 'order_success'] },
  { key: 'Navigation', events: ['nav_open', 'page_transition', 'open_modal', 'accordion'] },
  { key: 'Selection', events: ['select_color', 'select_size', 'image_switch', 'click'] },
  { key: 'Preferences', events: ['theme_toggle', 'lang_toggle'] },
  { key: 'Feedback', events: ['error'] },
];

export default function AdminSounds() {
  const { t } = useTranslation();
  const { lang } = useApp();
  const dir = lang === 'ar' ? 'rtl' : 'ltr';
  const [settings, setSettings] = useState<any>(null);
  const [tracks, setTracks] = useState<Track[]>([]);
  const [sfx, setSfx] = useState<Sfx[]>([]);
  const [eventMap, setEventMap] = useState<Record<string, string | null>>({});
  const [search, setSearch] = useState('');
  const [previewing, setPreviewing] = useState<string | null>(null);
  const [uploadingTrack, setUploadingTrack] = useState(false);
  const [uploadingSfx, setUploadingSfx] = useState(false);
  const trackInputRef = useRef<HTMLInputElement | null>(null);
  const sfxInputRef = useRef<HTMLInputElement | null>(null);
  const previewRef = useRef<HTMLAudioElement | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<{ table: 'sound_tracks' | 'sound_effects'; id: string; name: string } | null>(null);
  const [deleting, setDeleting] = useState(false);


  const reload = async () => {
    const [s, tr, fx, em] = await Promise.all([
      settingsApi.adminGet(),
      soundsApi.adminTracks(),
      soundsApi.adminEffects(),
      soundsApi.adminEventMap(),
    ]);
    setSettings(s.settings);
    setTracks(readItems<Track>(tr));
    setSfx(readItems<Sfx>(fx));
    const map: Record<string, string | null> = {};
    readItems<EventMap>(em).forEach((m: EventMap) => { map[m.event_key] = m.sound_effect_id; });
    setEventMap(map);
  };

  useEffect(() => { reload(); }, []);

  const [settingsDirty, setSettingsDirty] = useState(false);

  const updateLocalSettings = (patch: any) => {
    setSettings({ ...settings, ...patch });
    setSettingsDirty(true);
  };

  const saveGlobalSettings = async () => {
    try {
      await settingsApi.update(settings);
      setSettingsDirty(false);
      toast.success('Settings saved');
    } catch (e: any) { toast.error(e?.message || 'Failed to save'); }
  };

  const getDuration = (url: string) => new Promise<number>(res => {
    const a = new Audio(url);
    a.addEventListener('loadedmetadata', () => res(a.duration || 0));
    a.addEventListener('error', () => res(0));
  });

  const uploadTrack = async (file: File) => {
    setUploadingTrack(true);
    try {
      const url = await uploadImage('sounds', file, 'tracks/');
      const dur = await getDuration(url);
      const max = tracks.reduce((m, t) => Math.max(m, t.sort_order), -1);
      await soundsApi.createTrack({
        name: file.name.replace(/\.[^.]+$/, ''), fileUrl: url, durationSeconds: dur, sortOrder: max + 1,
      });
      toast.success('Track added'); reload();
    } catch (e: any) { toast.error(e.message); }
    finally { setUploadingTrack(false); }
  };

  const uploadSfx = async (file: File) => {
    setUploadingSfx(true);
    try {
      const url = await uploadImage('sounds', file, 'sfx/');
      const dur = await getDuration(url);
      await soundsApi.createEffect({
        name: file.name.replace(/\.[^.]+$/, ''), fileUrl: url, durationSeconds: dur,
      });
      toast.success('Effect added'); reload();
    } catch (e: any) { toast.error(e.message); }
    finally { setUploadingSfx(false); }
  };

  const renameRow = async (table: 'sound_tracks' | 'sound_effects', id: string, name: string) => {
    if (table === 'sound_tracks') await soundsApi.updateTrack(id, { name });
    else await soundsApi.updateEffect(id, name);
    reload();
  };
  const toggleTrack = async (id: string, v: boolean) => {
    await soundsApi.updateTrack(id, { isEnabled: v }); reload();
  };
  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      if (deleteTarget.table === 'sound_tracks') await soundsApi.removeTrack(deleteTarget.id);
      else await soundsApi.removeEffect(deleteTarget.id);
      setDeleteTarget(null);
      reload();
    } catch (e: any) {
      toast.error(e?.message || 'Delete failed');
    } finally {
      setDeleting(false);
    }
  };
  const move = async (idx: number, d: -1 | 1) => {
    const j = idx + d; if (j < 0 || j >= tracks.length) return;
    const a = tracks[idx], b = tracks[j];
    await Promise.all([
      soundsApi.updateTrack(a.id, { sortOrder: b.sort_order }),
      soundsApi.updateTrack(b.id, { sortOrder: a.sort_order }),
    ]);
    reload();
  };
  const setEvent = async (event_key: EventKey, sound_effect_id: string | null) => {
    setEventMap(m => ({ ...m, [event_key]: sound_effect_id }));
    await soundsApi.setEvent(event_key, sound_effect_id);
  };

  const playPreview = (url: string, id: string) => {
    if (previewRef.current) { previewRef.current.pause(); previewRef.current = null; }
    if (previewing === id) { setPreviewing(null); return; }
    const resolvedUrl = resolveAssetUrl(url);
    const a = new Audio(resolvedUrl);
    a.play().catch(() => toast.error('Cannot play audio'));
    previewRef.current = a; setPreviewing(id);
    a.onended = () => setPreviewing(null);
  };

  if (!settings) return (
    <div className="space-y-6">
      <h1 className="font-display text-3xl font-bold">{t('admin.nav.sounds')}</h1>
      <FormSkeleton rows={6} />
    </div>
  );

  const fmtDur = (s: number) => s > 0 ? `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, '0')}` : '—';
  const filtered = <T extends { name: string }>(arr: T[]) =>
    arr.filter(x => !search || x.name.toLowerCase().includes(search.toLowerCase()));

  const badgeSide: 'left' | 'right' = lang === 'ar' ? 'left' : 'right';
  const badgeTop = Number(settings.audio_badge_top_pct ?? 22);
  const badgeShrink = Number(settings.audio_badge_shrink_ms ?? 1000);

  return (
    <div className="space-y-6 max-w-6xl">
      {/* Header */}
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <h1 className="font-display text-3xl font-bold tracking-tight">{t('audio.sounds')}</h1>
          <p className="text-sm text-muted-foreground mt-1">
            {tracks.length} {t('audio.unitTracks')} · {sfx.length} {t('audio.unitEffects')} · {Object.values(eventMap).filter(Boolean).length} {t('audio.unitMapped')}
          </p>
        </div>
      </div>

      <Tabs defaultValue="overview" dir={dir}>
        <TabsList className="w-full !justify-start gap-1 overflow-visible">
          <Swiper
            modules={[FreeMode]}
            freeMode
            slidesPerView="auto"
            spaceBetween={4}
            dir="ltr"
            className="w-full"
          >
            <SwiperSlide className="!w-auto"><TabsTrigger value="overview"><Activity className="h-4 w-4 me-1.5 inline" />{t('audio.overview')}</TabsTrigger></SwiperSlide>
            <SwiperSlide className="!w-auto"><TabsTrigger value="music"><Music2 className="h-4 w-4 me-1.5 inline" />{t('audio.playlist')}</TabsTrigger></SwiperSlide>
            <SwiperSlide className="!w-auto"><TabsTrigger value="sfx"><Wand2 className="h-4 w-4 me-1.5 inline" />{t('audio.effects')}</TabsTrigger></SwiperSlide>
            <SwiperSlide className="!w-auto"><TabsTrigger value="events">{t('audio.events')}</TabsTrigger></SwiperSlide>
            <SwiperSlide className="!w-auto"><TabsTrigger value="badge">{t('audio.badge')}</TabsTrigger></SwiperSlide>
          </Swiper>
        </TabsList>


        {/* OVERVIEW */}
        <TabsContent value="overview" className="space-y-6 mt-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">{t('audio.globalSettings')}</CardTitle>
            </CardHeader>
            <CardContent className="grid md:grid-cols-3 gap-4">
              {[
                { k: 'audio_music_enabled', label: t('audio.musicEnabled') },
                { k: 'audio_sfx_enabled', label: t('audio.sfxEnabled') },
                { k: 'audio_loop_playlist', label: t('audio.loop') },
              ].map(({ k, label }) => (
                <div key={k} className="p-3 rounded-lg border border-border bg-muted/30">
                  <ToggleField
                    checked={!!settings[k]}
                    onCheckedChange={v => updateLocalSettings({ [k]: v })}
                    label={label}
                    variant="field"
                    spread
                  />
                </div>
              ))}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-lg">{t('audio.volumes')}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-5">
              {(['audio_master_volume', 'audio_music_volume', 'audio_sfx_volume'] as const).map((k, i) => (
                <div key={k}>
                  <div className="flex justify-between mb-2">
                    <Label>{t(['audio.master', 'audio.music', 'audio.sfx'][i])}</Label>
                    <span className="text-xs text-muted-foreground tabular-nums">{Math.round((settings[k] || 0) * 100)}%</span>
                  </div>
                  <Slider dir={dir} value={[(settings[k] || 0) * 100]} max={100}
                    onValueChange={(v) => setSettings({ ...settings, [k]: v[0] / 100 })}
                    onValueCommit={(v) => updateLocalSettings({ [k]: v[0] / 100 })} />
                </div>
              ))}
            </CardContent>
          </Card>
          <div className="flex justify-end">
            <Button disabled={!settingsDirty} onClick={saveGlobalSettings}>
              {t('common.save') || 'Save'}
            </Button>
          </div>
        </TabsContent>

        {/* MUSIC */}
        <TabsContent value="music" className="space-y-4 mt-6">
          <Card className="overflow-hidden">
            <CardHeader className="flex-row items-center justify-between space-y-0 flex-wrap gap-3">
              <CardTitle className="text-lg">{t('audio.playlist')}</CardTitle>
              <input ref={trackInputRef} type="file" accept="audio/*" hidden
                onChange={e => { const f = e.target.files?.[0]; if (f) uploadTrack(f); e.target.value = ''; }} />
              <Button onClick={() => trackInputRef.current?.click()} disabled={uploadingTrack}>
                {uploadingTrack
                  ? <><Loader2 className="h-4 w-4 me-2 animate-spin" /> {t('audio.uploading')}</>
                  : <><Upload className="h-4 w-4 me-2" /> {t('audio.upload')}</>}
              </Button>
            </CardHeader>
            <CardContent className="space-y-2">
              <Input placeholder={t('common.search')} value={search} onChange={e => setSearch(e.target.value)} className="max-w-sm mb-3" />
              {filtered(tracks).map((tr, idx) => (
                <div key={tr.id} className="rounded-lg border border-border bg-card hover:border-primary/40 transition-colors p-2 sm:p-3 min-w-0">
                  <div className="flex items-center gap-2 min-w-0">
                    <Button size="icon" variant="ghost" className="shrink-0" onClick={() => playPreview((tr as any).fileUrl || tr.file_url, tr.id)}>
                      {previewing === tr.id ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
                    </Button>
                    <Input value={tr.name} onChange={e => setTracks(tracks.map(t => t.id === tr.id ? { ...t, name: e.target.value } : t))}
                      onBlur={e => renameRow('sound_tracks', tr.id, e.target.value)}
                      className="border-0 bg-transparent focus-visible:bg-muted/50 min-w-0 flex-1" />
                  </div>
                  <div className="flex items-center justify-between gap-2 mt-1 ps-1">
                    <span className="text-xs text-muted-foreground tabular-nums">{fmtDur(tr.duration_seconds)}</span>
                    <div className="flex items-center gap-1">
                      <ToggleField checked={tr.is_enabled} onCheckedChange={v => toggleTrack(tr.id, v)} hideLabel />
                      <Button size="icon" variant="ghost" onClick={() => move(idx, -1)} disabled={idx === 0}><ArrowUp className="h-4 w-4" /></Button>
                      <Button size="icon" variant="ghost" onClick={() => move(idx, 1)} disabled={idx === tracks.length - 1}><ArrowDown className="h-4 w-4" /></Button>
                      <Button size="icon" variant="ghost" onClick={() => setDeleteTarget({ table: 'sound_tracks', id: tr.id, name: tr.name })}><Trash2 className="h-4 w-4 text-destructive" /></Button>
                    </div>
                  </div>
                </div>
              ))}
              {tracks.length === 0 && <p className="text-sm text-muted-foreground py-6 text-center">{t('audio.noTracks')}</p>}
            </CardContent>
          </Card>
        </TabsContent>

        {/* SFX */}
        <TabsContent value="sfx" className="space-y-4 mt-6">
          <Card className="overflow-hidden">
            <CardHeader className="flex-row items-center justify-between space-y-0 flex-wrap gap-3">
              <CardTitle className="text-lg">{t('audio.sfxLibrary')}</CardTitle>
              <input ref={sfxInputRef} type="file" accept="audio/*" hidden
                onChange={e => { const f = e.target.files?.[0]; if (f) uploadSfx(f); e.target.value = ''; }} />
              <Button onClick={() => sfxInputRef.current?.click()} disabled={uploadingSfx}>
                {uploadingSfx
                  ? <><Loader2 className="h-4 w-4 me-2 animate-spin" /> {t('audio.uploading')}</>
                  : <><Upload className="h-4 w-4 me-2" /> {t('audio.upload')}</>}
              </Button>
            </CardHeader>
            <CardContent className="space-y-2">
              <Input placeholder={t('common.search')} value={search} onChange={e => setSearch(e.target.value)} className="max-w-sm mb-3" />
              <div className="space-y-2">
                {filtered(sfx).map(s => {
                  const mappedEvents = Object.entries(eventMap).filter(([, v]) => v === s.id).map(([k]) => k);
                  return (
                    <Collapse
                      key={s.id}
                      variant="inner"
                      left={
                        <Button size="icon" variant="ghost" className="shrink-0" data-no-sfx
                          onClick={(e) => { e.stopPropagation(); playPreview((s as any).fileUrl || s.file_url, s.id); }}>
                          {previewing === s.id ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
                        </Button>
                      }
                      title={s.name}
                      right={
                        <>
                          <span className="text-xs text-muted-foreground tabular-nums shrink-0">{fmtDur(s.duration_seconds)}</span>
                          <Button size="icon" variant="ghost" className="shrink-0" data-no-sfx
                            onClick={(e) => { e.stopPropagation(); setDeleteTarget({ table: 'sound_effects', id: s.id, name: s.name }); }}>
                            <Trash2 className="h-4 w-4 text-destructive" />
                          </Button>
                        </>
                      }
                    >
                      <div className="space-y-3">
                        <div>
                          <Label className="text-xs text-muted-foreground mb-1.5 block">{t('common.name') || 'Name'}</Label>
                          <Input value={s.name} onChange={e => setSfx(sfx.map(x => x.id === s.id ? { ...x, name: e.target.value } : x))}
                            onBlur={e => renameRow('sound_effects', s.id, e.target.value)} />
                        </div>
                        <div>
                          <Label className="text-xs text-muted-foreground mb-1.5 block">{t('audio.mappedEvents') || 'Mapped events'}</Label>
                          {mappedEvents.length ? (
                            <div className="flex flex-wrap gap-1.5">
                              {mappedEvents.map(ev => <Badge key={ev} variant="secondary" className="text-[10px]">{ev.replace(/_/g, ' ')}</Badge>)}
                            </div>
                          ) : <span className="text-xs text-muted-foreground">—</span>}
                        </div>
                      </div>
                    </Collapse>
                  );
                })}
              </div>
              {sfx.length === 0 && <p className="text-sm text-muted-foreground py-6 text-center">{t('audio.noEffects')}</p>}
            </CardContent>
          </Card>
        </TabsContent>

        {/* EVENTS */}
        <TabsContent value="events" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">{t('audio.eventMap')}</CardTitle>
              <CardDescription>{t('audio.eventMapDesc')}</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {EVENT_GROUPS.map(group => (
                  <Collapse
                    key={group.key}
                    variant="outer"
                    defaultOpen
                    title={
                      <span className="flex items-center gap-2">
                        <span className="font-semibold">{t(`audio.groups.${group.key}`)}</span>
                        <Badge variant="secondary" className="text-xs">{group.events.length}</Badge>
                      </span>
                    }
                  >
                    <div className="space-y-2">
                      {group.events.map(ev => {
                        const mapped = eventMap[ev];
                        const mappedSfx = sfx.find(x => x.id === mapped);
                        const url = mappedSfx?.file_url;
                        const evLabel = t(`audio.eventLabels.${ev}`);
                        return (
                          <Collapse
                            key={ev}
                            variant="inner"
                            title={evLabel}
                            right={
                              <Badge
                                variant={mappedSfx ? 'secondary' : 'outline'}
                                className="text-[10px] max-w-[160px] truncate inline-block"
                              >
                                {mappedSfx?.name || t('audio.noSound')}
                              </Badge>
                            }
                          >
                            <Collapse
                              variant="nested"
                              defaultOpen
                              title={t('audio.assignSound')}
                              right={url ? (
                                <Button size="icon" variant="ghost" className="h-6 w-6" data-no-sfx
                                  onClick={(e) => { e.stopPropagation(); url && playPreview(url, `ev-${ev}`); }}>
                                  {previewing === `ev-${ev}` ? <Pause className="h-3.5 w-3.5" /> : <Play className="h-3.5 w-3.5" />}
                                </Button>
                              ) : undefined}
                            >
                              <div className="space-y-1">
                                <button
                                  type="button"
                                  data-no-sfx
                                  onClick={() => setEvent(ev, null)}
                                  className={cn(
                                    "w-full flex items-center justify-between gap-2 px-2.5 py-1.5 rounded-md text-xs text-start transition-colors",
                                    !mapped
                                      ? "bg-primary/15 text-primary border border-primary/40"
                                      : "border border-transparent hover:bg-muted/50"
                                  )}
                                >
                                  <span className="truncate">{t('audio.noSound')}</span>
                                  {!mapped && <Check className="h-3.5 w-3.5 shrink-0" />}
                                </button>
                                {sfx.map(s => {
                                  const selected = mapped === s.id;
                                  return (
                                    <button
                                      key={s.id}
                                      type="button"
                                      data-no-sfx
                                      onClick={() => setEvent(ev, s.id)}
                                      className={cn(
                                        "w-full flex items-center justify-between gap-2 px-2.5 py-1.5 rounded-md text-xs text-start transition-colors",
                                        selected
                                          ? "bg-primary/15 text-primary border border-primary/40"
                                          : "border border-transparent hover:bg-muted/50"
                                      )}
                                    >
                                      <span className="truncate">{s.name}</span>
                                      {selected && <Check className="h-3.5 w-3.5 shrink-0" />}
                                    </button>
                                  );
                                })}
                                {sfx.length === 0 && (
                                  <p className="text-xs text-muted-foreground px-2 py-1.5">{t('audio.noEffects')}</p>
                                )}
                              </div>
                            </Collapse>
                          </Collapse>
                        );
                      })}
                    </div>
                  </Collapse>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* BADGE */}
        <TabsContent value="badge" className="space-y-6 mt-6">
          <div className="grid lg:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">{t('audio.badge')}</CardTitle>
                <CardDescription>{t('audio.badgeDesc')}</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="rounded-lg border border-border bg-muted/30 p-3 text-xs text-muted-foreground">
                  {t('audio.sideAuto') || 'Position: auto — right for English, left for Arabic.'}
                </div>


                <div>
                  <div className="flex justify-between mb-2">
                    <Label>{t('audio.badgeTop')}</Label>
                    <span className="text-xs text-muted-foreground tabular-nums">{badgeTop}%</span>
                  </div>
                  <Slider dir={dir} value={[badgeTop]} min={5} max={60} step={1}
                    onValueChange={v => setSettings({ ...settings, audio_badge_top_pct: v[0] })}
                    onValueCommit={v => updateLocalSettings({ audio_badge_top_pct: v[0] })} />
                </div>

                <div>
                  <div className="flex justify-between mb-2">
                    <Label>{t('audio.badgeShrink')}</Label>
                    <span className="text-xs text-muted-foreground tabular-nums">{(badgeShrink / 1000).toFixed(1)}s</span>
                  </div>
                  <Slider dir={dir} value={[badgeShrink]} min={500} max={3000} step={500}
                    onValueChange={v => setSettings({ ...settings, audio_badge_shrink_ms: v[0] })}
                    onValueCommit={v => updateLocalSettings({ audio_badge_shrink_ms: v[0] })} />
                  <div className="flex justify-between text-[10px] text-muted-foreground mt-1 tabular-nums">
                    <span>0.5s</span><span>1s</span><span>2s</span><span>3s</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-lg">{t('audio.livePreview')}</CardTitle>
                <CardDescription>{t('audio.livePreviewDesc')}</CardDescription>
              </CardHeader>
              <CardContent>
                <BadgePreview side={badgeSide} topPct={badgeTop} shrinkMs={badgeShrink} key={`${badgeSide}-${badgeTop}-${badgeShrink}`} />
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>

      <AlertDialog open={!!deleteTarget} onOpenChange={(v) => !v && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-destructive">
              <Trash2 className="w-5 h-5" />
              {t('admin.deleteSound')}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {deleteTarget && t('admin.deleteSoundConfirm', { name: deleteTarget.name })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>{t('admin.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => { e.preventDefault(); confirmDelete(); }}
              disabled={deleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleting ? <Loader2 className="w-4 h-4 animate-spin" /> : t('admin.delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function BadgePreview({ side, topPct, shrinkMs }: { side: 'left' | 'right'; topPct: number; shrinkMs: number }) {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(true);
  const [muted, setMuted] = useState(false);
  useEffect(() => {
    setExpanded(true);
    const id = setTimeout(() => setExpanded(false), Math.max(200, shrinkMs));
    return () => clearTimeout(id);
  }, [shrinkMs, side, topPct]);

  const Icon = muted ? VolumeX : Volume2;
  const label = muted ? t('audio.unmute') : t('audio.mute');

  return (
    <div className="relative aspect-video rounded-lg border border-dashed border-border bg-gradient-to-br from-muted/40 to-background overflow-hidden">
      <div className="absolute inset-0 flex items-center justify-center text-xs text-muted-foreground">{t('audio.previewViewport')}</div>
      <motion.button
        onClick={() => setMuted(m => !m)}
        className={`absolute z-10 ${side === 'left' ? 'left-3' : 'right-3'} flex items-center gap-2 px-2.5 h-9 rounded-full
          bg-background/80 backdrop-blur-xl border border-primary/40 text-primary shadow-[0_0_15px_hsl(var(--primary)/0.3)]`}
        style={{ top: `${topPct}%` }}
        animate={{ scale: 1 }}
      >
        <Icon className="h-4 w-4 shrink-0" />
        <AnimatePresence initial={false}>
          {expanded && (
            <motion.span
              initial={{ opacity: 0, width: 0 }}
              animate={{ opacity: 1, width: 'auto' }}
              exit={{ opacity: 0, width: 0 }}
              className="text-[10px] font-semibold uppercase tracking-wider whitespace-nowrap overflow-hidden"
            >
              {label}
            </motion.span>
          )}
        </AnimatePresence>
      </motion.button>
    </div>
  );
}
